import { Spinner } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import createAxios from "@/lib/axios";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ClockIcon,
  CookingPotIcon,
  ExternalLinkIcon,
  MessageCirclePlusIcon,
  NotebookTextIcon,
  SearchCheckIcon,
} from "lucide-react";

export const Route = createFileRoute("/dashboard/")({
  component: Home,
});

function Home() {
  const { isLoaded, user } = useUser();
  if (!isLoaded) return <Spinner />;

  return (
    <main className="flex w-full flex-col">
      <p className="w-full pb-4 pl-6 pt-12 text-center text-4xl font-semibold">
        Welcome{user && ", " + user.firstName}
      </p>
      <div className="light grid size-full grid-cols-2 grid-rows-2 gap-4 p-4">
        <div
          className="rounded-md text-black"
          style={{ backgroundColor: "hsl(252, 100%, 99.5%)" }}
        >
          <h1 className="flex w-full items-center justify-center gap-4 p-6 text-center text-4xl font-bold">
            <SearchCheckIcon className="size-8 stroke-[2.5]" />
            <span>Evaluations</span>
          </h1>
          <div className="flex items-center justify-between p-3">
            <p className="flex items-center gap-2 pl-1">
              <ClockIcon className="size-4 text-purple-950" />
              <span>Recent Evaluations</span>
            </p>
            <Link to="/dashboard/evaluate/new">
              <Button className="flex items-center gap-2 bg-purple-950">
                <SearchCheckIcon className="size-5" />
                <span>Create New Evaluation</span>
              </Button>
            </Link>
          </div>
          <EvalRecents />
        </div>
        <div className="rounded-md bg-[#faffff] text-black">
          <h1 className="flex w-full items-center justify-center gap-4 p-6 text-center text-4xl font-bold">
            <MessageCirclePlusIcon className="size-8 stroke-[2.5]" />
            <span>Quizzes</span>
          </h1>
          <div className="flex items-center justify-between p-3">
            <p className="flex items-center gap-2 pl-1">
              <ClockIcon className="size-4 text-cyan-900" />
              <span>Recent Quizzes</span>
            </p>
            <Link to="/dashboard/quiz/new/select-subject">
              <Button className="flex items-center gap-2 bg-cyan-900">
                <MessageCirclePlusIcon className="size-5" />
                <span>Create New Quiz</span>
              </Button>
            </Link>
          </div>
          <QuizRecents />
        </div>
        <div className="rounded-md bg-[#fffafa] text-black">
          <h1 className="flex w-full items-center justify-center gap-4 p-6 text-center text-4xl font-bold">
            <NotebookTextIcon className="size-8 stroke-[2.5]" />
            <span>Course Material</span>
          </h1>
          <div className="flex items-center justify-between p-3">
            <p className="flex items-center gap-2 pl-1">
              <ClockIcon className="size-4 text-rose-900" />
              <span>Recent Course Materials</span>
            </p>
            <Link to="/dashboard/presentation/new/select-subject">
              <Button className="flex items-center gap-2 bg-rose-900">
                <NotebookTextIcon className="size-5" />
                <span>Create New Course Material</span>
              </Button>
            </Link>
          </div>
          <PPTRecents />
        </div>
        <div className="rounded-md bg-[#fffefa] text-black">
          <h1 className="w-full p-6 text-center text-4xl font-bold">
            Analytics
          </h1>
          <p className="flex items-center gap-2 p-6">
            <CookingPotIcon className="size-5 text-amber-700" />
            <span>Analytics coming soon...</span>
          </p>
        </div>
      </div>
    </main>
  );
}

function formatDate(date: Date) {
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", {
    month: "long",
  });

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return day + "th";
    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };

  return `${getOrdinalSuffix(day)} ${month}`;
}
function parseCustomDate(dateString: string) {
  const formattedIsoString = dateString.replace("IST+0530", "+05:30");

  // Create a new Date object
  const date = new Date(formattedIsoString);

  return date;
}

function QuizRecents() {
  const { data, isLoading } = useQuery({
    queryKey: ["quiz", "recents"],
    queryFn: async () => {
      const client = await createAxios();
      const res = await client.get<
        {
          _id: string;
          subjectName: string;
          createdAt: string;
        }[]
      >(import.meta.env.VITE_BACKEND_URL + "/qa-sets?limit=4");
      return res.data || [];
    },
  });
  if (isLoading || !data)
    return (
      <div className="light flex h-[40vh] items-center justify-center rounded-md bg-white text-foreground">
        <div className="flex flex-col items-center gap-4">
          <Label>Loading recent quizzes...</Label>
          <Spinner className="text-black" />
        </div>
      </div>
    );
  return (
    <div className="h-full p-2">
      <div className="min-h-[40vh] rounded-md border-2 border-muted-foreground">
        <Table className="light text-black">
          <TableHeader>
            <TableHead className="grow">ID</TableHead>
            <TableHead className="w-20">Subject</TableHead>
            <TableHead className="w-28">Created At</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((quiz) => (
                <TableRow key={quiz._id}>
                  <TableCell>{quiz._id}</TableCell>
                  <TableCell>
                    <Badge className="bg-cyan-900">
                      {quiz.subjectName?.slice(0, 3)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {formatDate(parseCustomDate(quiz.createdAt))}
                  </TableCell>
                  <TableCell>
                    <Link to={"/dashboard/quiz/$id"} params={{ id: quiz._id }}>
                      <Button className="flex w-full items-center gap-2 bg-cyan-900">
                        <span>View</span>
                        <ExternalLinkIcon className="size-3" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableCell>No quizzes found</TableCell>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function EvalRecents() {
  const { data, isLoading } = useQuery({
    queryKey: ["evaluation", "recents"],
    queryFn: async () => {
      const client = await createAxios();
      const res = await client.get<
        {
          id: string;
          name: string;
          created_at: string;
          progress: {
            finished: number;
            total: number;
            error: number;
          };
        }[]
      >(import.meta.env.VITE_BACKEND_URL + "/evaluations?limit=4");
      return res.data || [];
    },
  });
  if (isLoading)
    return (
      <div className="light flex h-[40vh] items-center justify-center rounded-md bg-white text-foreground">
        <div className="flex flex-col items-center gap-4">
          <Label>Loading recent evaluations...</Label>
          <Spinner className="text-black" />
        </div>
      </div>
    );
  return (
    <div className="h-full p-2">
      <div className="min-h-[40vh] rounded-md border-2 border-muted-foreground">
        <Table className="light text-black">
          <TableHeader>
            <TableHead className="grow">Job Name</TableHead>
            <TableHead className="w-20">Progress</TableHead>
            <TableHead className="w-28">Created At</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((job) => (
                <TableRow key={job.name}>
                  <TableCell>{job.name}</TableCell>
                  <TableCell>
                    <Badge className="bg-purple-950">
                      {job.progress.finished / job.progress.total}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {formatDate(parseCustomDate(job.created_at || ""))}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={"/dashboard/evaluate/$id"}
                      params={{ id: job.id }}
                    >
                      <Button className="flex w-full items-center gap-2 bg-purple-950">
                        <span>View</span>
                        <ExternalLinkIcon className="size-3" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableCell>No evaluations found</TableCell>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PPTRecents() {
  const { data, isLoading } = useQuery({
    queryKey: ["ppt", "recents"],
    queryFn: async () => {
      const client = await createAxios();
      const res = await client.get<
        {
          id: string;
          name: string;
          start_time: string;
          end_time: string;
          status: "processing" | "ppt_generated" | "completed";
        }[]
      >(import.meta.env.VITE_BACKEND_URL + "/presentations?limit=4");
      return res.data || [];
    },
  });
  if (isLoading)
    return (
      <div className="light flex h-[40vh] items-center justify-center rounded-md bg-white text-foreground">
        <div className="flex flex-col items-center gap-4">
          <Label>Loading recent course materials...</Label>
          <Spinner className="text-black" />
        </div>
      </div>
    );
  return (
    <div className="h-full p-2">
      <div className="min-h-[40vh] rounded-md border-2 border-muted-foreground">
        <Table className="light text-black">
          <TableHeader>
            <TableHead className="grow">Name</TableHead>
            <TableHead className="w-20">Status</TableHead>
            <TableHead className="w-28">Created At</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((ppt) => (
                <TableRow key={ppt.id}>
                  <TableCell>{ppt.name}</TableCell>
                  <TableCell>
                    <Badge className="bg-rose-900">
                      {ppt.status == "processing"
                        ? "Processing"
                        : ppt.status == "ppt_generated"
                          ? "Generated"
                          : ppt.status == "completed"
                            ? "Completed"
                            : "Invalid"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {formatDate(parseCustomDate(ppt.start_time || ""))}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={"/dashboard/presentation/$id"}
                      params={{ id: ppt.id }}
                    >
                      <Button className="flex w-full items-center gap-2 bg-rose-900">
                        <span>View</span>
                        <ExternalLinkIcon className="size-3" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableCell>No course materials found</TableCell>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
